/**
 * Link hygiene for the TinyMCE article editors.
 *
 * Root cause of ~2,860 broken article links on the website: editors paste a
 * headline + URL (or "…Read more at: https://…", or WhatsApp text) into the
 * link dialog's URL field. TinyMCE's URL normaliser then treats the string as
 * a relative path and collapses `https://` to `https:/`, and the browser later
 * resolves the whole thing relative to /details/, producing 404 URLs that
 * Google crawls by the hundred.
 *
 * Two layers:
 *  - `editorUrlConverter` is TinyMCE's `urlconverter_callback`: it fixes a URL
 *    at the moment it is inserted (links and images).
 *  - `sanitizeArticleLinks` runs over the saved HTML as a safety net.
 */
const ABS_URL = /https?:\/+[^\s"'<>]+/i;
const PROPER = /^(https?:\/\/[^\s"']+|\/[^\s"']*|#[^\s"']*|mailto:[^\s"']+|tel:[^\s"']+)$/i;

/** True for suprabhaatham.com URLs; any other http(s) URL belongs to another site. */
const isSiteUrl = (u: string): boolean => /^https?:\/+([a-z0-9-]+\.)*suprabhaatham\.com(?:[/:?#]|$)/i.test(u);

/** Returns a usable href, or "" when nothing link-like can be recovered. */
export const cleanHref = (href: string): string => {
  const raw = (href || "").trim();
  // A well-formed URL on another site is never touched: its path may legitimately contain
  // "details/<digits>", and it must not be re-pointed at one of our articles.
  if (/^https?:\/\/\S+$/i.test(raw) && !isSiteUrl(raw)) return raw;
  const url = raw.match(ABS_URL)?.[0];
  // Same for a foreign URL pasted after headline text: keep that URL, only fix "https:/".
  if (url && !isSiteUrl(url)) return url.replace(/^(https?:)\/+/i, "$1//");
  const id = raw.match(/details\/[^0-9]*?(\d+)/)?.[1];
  if (id) return `https://www.suprabhaatham.com/details/${id}`;
  if (PROPER.test(raw)) return raw;
  return url ? url.replace(/^(https?:)\/+/i, "$1//") : "";
};

/** TinyMCE `urlconverter_callback` signature: (url, node, on_save, name) => string */
export const editorUrlConverter = (url: string): string => cleanHref(url) || url;

/** Repair every <a href> in an HTML string; drop hrefs that can't be repaired. */
export const sanitizeArticleLinks = (html: string): string =>
  (html || "").replace(/href=(["'])([\s\S]*?)\1/gi, (m, q, href: string) => { // value runs to the next quote of the SAME kind, so ' inside "..." (or " inside '...') is matched
    const fixed = cleanHref(href);
    return fixed ? `href=${q}${fixed}${q}` : "";
  });
